import { ArrowRightLeft, CreditCard, Download, Plus, Receipt, RotateCcw, ShoppingBag } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { TransactionType } from "../types/card-movements.types";

export type TransactionTypeMeta = {
  icon: LucideIcon;
  iconWrapper: string;
  /** I18n key (cards namespace) for the human-readable type label. */
  tKey: string;
};

/**
 * Presentation metadata per transaction type: icon, badge styling and the i18n key for its label. Shared by
 * the movements list and the detail drawer so both render the same icon/label for a given type.
 */
export const TRANSACTION_TYPE_META: Record<TransactionType, TransactionTypeMeta> = {
  PURCHASE: {
    icon: ShoppingBag,
    iconWrapper: "bg-primary/10 text-primary",
    tKey: "txn_type_purchase",
  },
  TOPUP: {
    icon: Plus,
    iconWrapper: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    tKey: "txn_type_topup",
  },
  DEPOSIT: {
    icon: Plus,
    iconWrapper: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    tKey: "txn_type_deposit",
  },
  REFUND: {
    icon: RotateCcw,
    iconWrapper: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    tKey: "txn_type_refund",
  },
  ATM_WITHDRAWAL: {
    icon: CreditCard,
    iconWrapper: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300",
    tKey: "txn_type_atm_withdrawal",
  },
  WITHDRAWAL: {
    icon: Download,
    iconWrapper: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300",
    tKey: "txn_type_withdrawal",
  },
  FEE: {
    icon: Receipt,
    iconWrapper: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    tKey: "txn_type_fee",
  },
  TRANSFER: {
    icon: ArrowRightLeft,
    iconWrapper: "bg-primary/10 text-primary",
    tKey: "txn_type_transfer",
  },
  REVERSAL: {
    icon: RotateCcw,
    iconWrapper: "bg-muted text-muted-foreground",
    tKey: "txn_type_reversal",
  },
  ADJUSTMENT: {
    icon: RotateCcw,
    iconWrapper: "bg-muted text-muted-foreground",
    tKey: "txn_type_adjustment",
  },
};

export const FALLBACK_TYPE_META: TransactionTypeMeta = {
  icon: ShoppingBag,
  iconWrapper: "bg-muted text-muted-foreground",
  tKey: "txn_type_purchase",
};

export const getTransactionTypeMeta = (
  type: TransactionType,
  topUpSource?: "COP_BALANCE" | null,
): TransactionTypeMeta => {
  const meta = TRANSACTION_TYPE_META[type] ?? FALLBACK_TYPE_META;
  return type === "DEPOSIT" && topUpSource === "COP_BALANCE"
    ? { ...meta, tKey: "txn_type_deposit_cop" }
    : meta;
};

const localeFor = (lang: string): string => {
  switch (lang) {
    case "es":
      return "es-ES";
    case "pt":
      return "pt-BR";
    default:
      return "en-US";
  }
};

/** Localized country name from an ISO 3166-1 alpha-2 code. Returns the raw code on failure. */
export const getCountryName = (code: string | null | undefined, lang: string): string | null => {
  if (!code) return null;
  try {
    const display = new Intl.DisplayNames([localeFor(lang)], { type: "region" });
    return display.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};

/** Intl currency formatting; falls back to "<amount> <currency>" for unknown codes. */
export const formatTransactionAmount = (amount: number, currency: string, lang: string): string => {
  try {
    return new Intl.NumberFormat(localeFor(lang), {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

/** Long date + time, e.g. "5 de mayo de 2026, 14:32". Returns "—" for missing input. */
export const formatTransactionDateTime = (raw: string | null | undefined, lang: string): string => {
  if (!raw) return "—";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const locale = localeFor(lang);
  const datePart = date.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  const timePart = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `${datePart}, ${timePart}`;
};

/**
 * Some providers report the raw rate as local-currency-per-USD, others as USD-per-local-currency (e.g. a BRL
 * purchase can carry a provider rate of ~0.195, which is actually "1 BRL = 0.195 USD", the inverse of the
 * ~5.12 BRL-per-USD rate we want to display). `amount`/`usd_amount` are always normalized absolute values, so
 * deriving the rate from them keeps the direction correct regardless of provider convention. Falls back to
 * the raw provider rate when `usdAmount` isn't available.
 */
export const resolveExchangeRate = (amount: number, usdAmount: number | null, rawRate: number): number => {
  if (usdAmount) {
    return Math.abs(amount) / Math.abs(usdAmount);
  }
  return rawRate;
};

/** 1 USD = X <currency>, locale-formatted. */
export const formatExchangeRate = (rate: number, currency: string, lang: string): string => {
  const formatted = new Intl.NumberFormat(localeFor(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(rate);
  return `1 USD = ${formatted} ${currency}`;
};
